/* ===== Settings Page (organization configuration, admin-only) ===== */

import { useEffect, useRef, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  Building,
  CreditCard,
  Save,
  LayoutGrid,
  Megaphone,
  Users,
  Calendar,
  Briefcase,
  MessageSquare,
  FileText,
  HeartHandshake,
  DollarSign,
  Handshake,
  ShoppingCart,
  ClipboardList,
} from 'lucide-react'
import {
  getOrgPaymentConnectionStatus,
  startMercadoPagoOAuth,
  type OrgPaymentConnectionStatus,
} from '@zetyar/supabase-client'
import { MODULE_DEFINITIONS, type OrgModuleKey } from '@zetyar/domain'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Avatar } from '@/presentation/components/ui/Avatar'
import { Badge } from '@/presentation/components/ui/Badge'
import { uploadImageAsset } from '@/infrastructure/storage/images'
import { getSupabaseClient } from '@/infrastructure/supabase/app'

const MP_STATUS_MESSAGE: Record<string, { text: string; ok: boolean }> = {
  connected: { text: 'Cuenta de Mercado Pago conectada con éxito.', ok: true },
  denied: { text: 'Conexión con Mercado Pago cancelada.', ok: false },
  error: { text: 'No se pudo conectar la cuenta de Mercado Pago. Intenta nuevamente.', ok: false },
}

const MODULE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Megaphone,
  Users,
  Calendar,
  Briefcase,
  MessageSquare,
  FileText,
  HeartHandshake,
  DollarSign,
  Handshake,
  ShoppingCart,
  ClipboardList,
  CreditCard,
}

export function SettingsPage() {
  const { currentOrganization, permissions, updateOrganization } = useApp()
  const canManageOrganization = permissions.includes('organization.manage')
  const [orgName, setOrgName] = useState(currentOrganization?.name || '')
  const [orgLogoUrl, setOrgLogoUrl] = useState(currentOrganization?.logoUrl || '')
  const [currency, setCurrency] = useState('CLP')
  const [timezone, setTimezone] = useState('America/Santiago')
  // Each organization decides which modules it uses; Zetyar can also adjust them from the SaaS panel.
  const [features, setFeatures] = useState<Record<string, boolean>>(currentOrganization?.features ?? {})
  const [isSaving, setIsSaving] = useState(false)
  const [isUploadingOrgLogo, setIsUploadingOrgLogo] = useState(false)
  const orgLogoInputRef = useRef<HTMLInputElement>(null)
  const [paymentStatus, setPaymentStatus] = useState<OrgPaymentConnectionStatus | null>(null)
  const [isConnectingMercadoPago, setIsConnectingMercadoPago] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const mpStatusNotice = searchParams.get('mp_status')

  useEffect(() => {
    if (currentOrganization) {
      setOrgName(currentOrganization.name || '')
      setOrgLogoUrl(currentOrganization.logoUrl || '')
      setTimezone(currentOrganization.timezone || 'America/Santiago')
      setCurrency(
        currentOrganization.locale === 'en-US'
          ? 'USD'
          : currentOrganization.locale === 'es-ES'
            ? 'EUR'
            : 'CLP',
      )
      setFeatures(currentOrganization.features ?? {})
    }
  }, [currentOrganization])

  useEffect(() => {
    if (!currentOrganization) return
    getOrgPaymentConnectionStatus(getSupabaseClient(), currentOrganization.id)
      .then(setPaymentStatus)
      .catch((error) => console.error('Error al consultar el estado de Mercado Pago:', error))
  }, [currentOrganization, mpStatusNotice])

  useEffect(() => {
    if (!mpStatusNotice) return
    const timeout = setTimeout(() => {
      searchParams.delete('mp_status')
      setSearchParams(searchParams, { replace: true })
    }, 6000)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpStatusNotice])

  if (!canManageOrganization) {
    return <Navigate to="/dashboard" replace />
  }

  const handleConnectMercadoPago = async () => {
    if (!currentOrganization) return
    try {
      setIsConnectingMercadoPago(true)
      const authorizeUrl = await startMercadoPagoOAuth(getSupabaseClient(), currentOrganization.id)
      window.location.href = authorizeUrl
    } catch (error) {
      console.error('Error al iniciar la conexión con Mercado Pago:', error)
      alert(error instanceof Error ? error.message : 'No se pudo iniciar la conexión con Mercado Pago.')
      setIsConnectingMercadoPago(false)
    }
  }

  // A missing flag means enabled, so the first toggle writes an explicit false.
  const handleToggleModule = (key: OrgModuleKey) => {
    setFeatures((current) => ({ ...current, [key]: current[key] === false }))
  }

  const handleSaveOrg = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await updateOrganization(orgName, timezone, currency, orgLogoUrl || null, features)
      alert('Configuración de la organización y módulos guardada.')
    } catch {
      alert('Error al guardar la configuración.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdateOrgLogo = (e: React.MouseEvent) => {
    e.preventDefault()
    orgLogoInputRef.current?.click()
  }

  const handleOrgLogoFile = async (file: File | undefined) => {
    if (!file || !currentOrganization) return
    try {
      setIsUploadingOrgLogo(true)
      const uploadedUrl = await uploadImageAsset({
        organizationId: currentOrganization.id,
        scope: 'branding',
        ownerId: currentOrganization.id,
        file,
        label: 'logo',
        maxBytes: 3 * 1024 * 1024,
      })
      setOrgLogoUrl(uploadedUrl)
      await updateOrganization(orgName, timezone, currency, uploadedUrl, features)
    } catch (error) {
      console.error('Error al subir el logo de la organización:', error)
      alert(error instanceof Error ? error.message : 'No se pudo subir el logo.')
    } finally {
      setIsUploadingOrgLogo(false)
    }
  }

  const selectClassName =
    'w-full h-10 px-3 rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20'

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Configuración</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Administra la información, módulos activos y conexiones de {currentOrganization?.name}.
        </p>
      </div>

      <form onSubmit={handleSaveOrg} className="space-y-6">
        {/* Datos Generales */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-5 h-5 text-primary-600" /> Perfil de la Organización
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Avatar src={orgLogoUrl} name={orgName} size="xl" />
              <input
                ref={orgLogoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => void handleOrgLogoFile(event.target.files?.[0])}
              />
              <div>
                <Button variant="outline" size="sm" onClick={handleUpdateOrgLogo} isLoading={isUploadingOrgLogo}>
                  Cambiar logo
                </Button>
                <p className="text-xs text-neutral-400 mt-1">JPG, PNG o WebP. Máximo 3 MB.</p>
              </div>
            </div>

            <Input
              label="Nombre de la organización"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Moneda de operación</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={selectClassName}
                >
                  <option value="CLP">CLP ($ Chile)</option>
                  <option value="USD">USD ($ Dólar estadounidense)</option>
                  <option value="EUR">EUR (Euro)</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Zona horaria</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className={selectClassName}
                >
                  <option value="America/Santiago">America/Santiago</option>
                  <option value="America/Buenos_Aires">America/Buenos_Aires</option>
                  <option value="America/Bogota">America/Bogota</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gestión de Módulos del Portal */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-primary-600" />
                <CardTitle className="text-base">Módulos del portal</CardTitle>
              </div>
              <span className="text-xs text-neutral-500 dark:text-neutral-400 font-normal">
                Activa los módulos que usan y oculta los que no. El cambio aplica a todos los miembros.
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MODULE_DEFINITIONS.map((module) => {
                const IconComponent = MODULE_ICONS[module.iconName] || LayoutGrid
                const isEnabled = features[module.key] !== false

                return (
                  <button
                    key={module.key}
                    type="button"
                    role="switch"
                    aria-checked={isEnabled}
                    onClick={() => handleToggleModule(module.key)}
                    className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 text-left transition-colors ${
                      isEnabled
                        ? 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 hover:border-primary-300 dark:hover:border-primary-700'
                        : 'bg-neutral-50/70 dark:bg-neutral-950/40 border-neutral-200/50 dark:border-neutral-900/60 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <span
                        className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                          isEnabled
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-950/60 dark:text-primary-300'
                            : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500'
                        }`}
                      >
                        <IconComponent className="h-4.5 w-4.5" />
                      </span>
                      <span className="min-w-0">
                        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 block truncate">
                          {module.label}
                        </span>
                        <span className="text-[11px] text-neutral-500 dark:text-neutral-400 block truncate">
                          {module.description}
                        </span>
                      </span>
                    </span>

                    <span
                      aria-hidden="true"
                      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                        isEnabled ? 'bg-primary-600' : 'bg-neutral-300 dark:bg-neutral-700'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          isEnabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </span>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Botón Guardar Cambios */}
        <div className="flex justify-end">
          <Button type="submit" variant="primary" size="lg" leftIcon={<Save className="w-4 h-4" />} isLoading={isSaving}>
            Guardar configuración y módulos
          </Button>
        </div>
      </form>

      {mpStatusNotice && MP_STATUS_MESSAGE[mpStatusNotice] && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            MP_STATUS_MESSAGE[mpStatusNotice].ok
              ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-500/30 dark:bg-success-500/10 dark:text-success-300'
              : 'border-danger-200 bg-danger-50 text-danger-700 dark:border-danger-500/30 dark:bg-danger-500/10 dark:text-danger-300'
          }`}
        >
          {MP_STATUS_MESSAGE[mpStatusNotice].text}
        </div>
      )}

      {/* Pasarela de Pagos Mercado Pago */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-600" /> Pagos y Cobros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-neutral-700 dark:text-neutral-300">
                Conecta la cuenta de Mercado Pago de tu organización para recibir aportes y ventas
                directamente.
              </p>
              {paymentStatus?.status === 'active' ? (
                <Badge variant="success" className="mt-2">
                  Conectada
                </Badge>
              ) : (
                <Badge variant="neutral" className="mt-2">
                  Sin conectar
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => void handleConnectMercadoPago()}
              isLoading={isConnectingMercadoPago}
            >
              {paymentStatus?.status === 'active' ? 'Reconectar Mercado Pago' : 'Conectar Mercado Pago'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
